import { useEffect, useState } from "react";
import { OwnerNav } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { SIZE_OPTIONS, COLOR_OPTIONS, STAT_DEFAULTS, TESTIMONIAL_DEFAULTS, buildSectionsForm, buildContentForm } from "@/lib/sections";
import { HoursEditor } from "@/components/HoursEditor";
import { StaffManager } from "@/components/StaffManager";

export default function Settings() {
  const [salon, setSalon] = useState(null);
  const [services, setServices] = useState([]);
  const [closures, setClosures] = useState([]);
  const [staff, setStaff] = useState([]);
  const [newClosure, setNewClosure] = useState({ title: "", date_start: "", date_end: "", all_day: true, time_start: "", time_end: "", visible: true });
  const [salonForm, setSalonForm] = useState({});
  const [emailForm, setEmailForm] = useState(null);
  const [emailPwdSet, setEmailPwdSet] = useState(false);
  const [testTo, setTestTo] = useState("");

  useEffect(() => {
    (async () => {
      const [sv, c, st, os] = await Promise.all([
        api.get("/owner/services"),
        api.get("/owner/closures"),
        api.get("/owner/staff"),
        api.get("/owner/salon"),
      ]);
      setSalon(os.data);
      setSalonForm({
        name: os.data.name, tagline: os.data.tagline, description: os.data.description,
        address: os.data.address, phone: os.data.phone, email: os.data.email,
        map_url: os.data.map_url, banner_url: os.data.banner_url,
        logo_url: os.data.logo_url,
        hero_eyebrow: os.data.hero_eyebrow || "Paris · Depuis 2024 · Sur rendez-vous",
        hero_title_line1: os.data.hero_title_line1 || "Coupe nette.",
        hero_title_line2: os.data.hero_title_line2 || "Rasage précis.",
        hero_title_line3: os.data.hero_title_line3 || "Style affirmé.",
        sections: buildSectionsForm(os.data),
        stats: (os.data.stats && os.data.stats.length) ? os.data.stats : STAT_DEFAULTS,
        testimonials: (os.data.testimonials && os.data.testimonials.length) ? os.data.testimonials : TESTIMONIAL_DEFAULTS,
        content: buildContentForm(os.data),
      });
      setServices(sv.data);
      setClosures(c.data);
      setStaff(st.data);
      setEmailForm({
        emails_enabled: os.data.emails_enabled || false,
        email_from_name: os.data.email_from_name || os.data.name || "",
        email_from_address: os.data.email_from_address || "",
        site_url: os.data.site_url || "",
        smtp_host: os.data.smtp_host || "",
        smtp_port: os.data.smtp_port || 587,
        smtp_security: os.data.smtp_security || "tls",
        smtp_user: os.data.smtp_user || "",
        smtp_password: "",
        owner_notify_email: os.data.owner_notify_email || "",
      });
      setEmailPwdSet(!!os.data.smtp_password_set);
    })();
  }, []);

  const saveSalon = async () => {
    try {
      const { data } = await api.patch("/owner/salon", salonForm);
      setSalon(data);
      toast.success("Modifications enregistrées");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveEmails = async () => {
    try {
      const payload = { ...emailForm };
      if (!payload.smtp_password) delete payload.smtp_password;
      const { data } = await api.patch("/owner/salon", payload);
      setEmailPwdSet(!!data.smtp_password_set);
      setEmailForm((f) => ({ ...f, smtp_password: "" }));
      toast.success("Configuration email enregistrée");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const sendTest = async () => {
    const to = testTo || emailForm.owner_notify_email;
    if (!to) { toast.error("Renseignez une adresse email de test"); return; }
    try {
      await api.post("/owner/email/test", { to });
      toast.success(`Email de test envoyé à ${to}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveHours = async (hours) => {
    try {
      const { data } = await api.patch("/owner/hours", { hours });
      setSalon(data);
      toast.success("Horaires enregistrés");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateService = async (id, patch) => {
    try {
      const { data } = await api.patch(`/owner/services/${id}`, patch);
      setServices(services.map(s => s.id === id ? data : s));
      toast.success("Prestation mise à jour");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addClosure = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newClosure };
      if (payload.all_day) { delete payload.time_start; delete payload.time_end; }
      const { data } = await api.post("/owner/closures", payload);
      setClosures([...closures, data]);
      setNewClosure({ title: "", date_start: "", date_end: "", all_day: true, time_start: "", time_end: "", visible: true });
      toast.success("Fermeture ajoutée");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteClosure = async (id) => {
    if (!window.confirm("Supprimer cette fermeture ?")) return;
    try {
      await api.delete(`/owner/closures/${id}`);
      setClosures(closures.filter(c => c.id !== id));
      toast.success("Supprimée");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Section title (eyebrow/title/size/color)
  const updateSection = (key, field, value) =>
    setSalonForm(f => ({ ...f, sections: { ...f.sections, [key]: { ...f.sections[key], [field]: value } } }));
  // Stats
  const updateStat = (i, field, value) =>
    setSalonForm(f => { const stats = [...f.stats]; stats[i] = { ...stats[i], [field]: value }; return { ...f, stats }; });
  // Testimonials
  const updateTestimonial = (i, field, value) =>
    setSalonForm(f => { const ts = [...f.testimonials]; ts[i] = { ...ts[i], [field]: value }; return { ...f, testimonials: ts }; });
  const addTestimonial = () =>
    setSalonForm(f => ({ ...f, testimonials: [...f.testimonials, { name: "", text: "", stars: 5 }] }));
  const removeTestimonial = (i) =>
    setSalonForm(f => ({ ...f, testimonials: f.testimonials.filter((_, idx) => idx !== i) }));
  // Rich content helpers (functional updates to avoid stale state)
  const cset = (key, val) => setSalonForm(f => ({ ...f, content: { ...f.content, [key]: val } }));
  const csetArr = (key, i, patch) => setSalonForm(f => {
    const arr = [...(f.content[key] || [])];
    arr[i] = (patch && typeof patch === "object" && !Array.isArray(patch)) ? { ...arr[i], ...patch } : patch;
    return { ...f, content: { ...f.content, [key]: arr } };
  });
  const caddArr = (key, item) => setSalonForm(f => ({ ...f, content: { ...f.content, [key]: [...(f.content[key] || []), item] } }));
  const cremoveArr = (key, i) => setSalonForm(f => ({ ...f, content: { ...f.content, [key]: (f.content[key] || []).filter((_, idx) => idx !== i) } }));

  if (!salon) return <div className="min-h-screen bg-[#0A0908] flex items-center justify-center text-[#A39E93]">Chargement…</div>;
  const content = salonForm.content || {};

  return (
    <div className="min-h-screen bg-[#0A0908] text-[#F4F0EA]">
      <OwnerNav />
      <Toaster theme="dark" />

      <div className="max-w-5xl mx-auto px-6 py-12 space-y-20">
        <div>
          <div className="text-[10px] tracking-[0.4em] text-[#C5A059] uppercase mb-2">Paramètres</div>
          <h1 className="font-serif text-4xl sm:text-5xl font-light">Votre salon.</h1>
          <p className="text-sm text-[#A39E93] mt-3">Les sections sont rangées dans l&apos;ordre d&apos;affichage de votre site, de haut en bas.</p>
        </div>

        {/* 01 — Bandeau d'accueil */}
        <section data-testid="settings-hero">
          <SectionHeader n="01" title="Bandeau d'accueil" desc="Le grand titre tout en haut de votre site (3 lignes ; la 2ᵉ ligne est dorée)." />
          <div className="grid grid-cols-1 gap-5 max-w-2xl">
            <Field label="Petit texte au-dessus (ex : Paris · Depuis 2024…)" value={salonForm.hero_eyebrow} onChange={(v)=>setSalonForm({...salonForm, hero_eyebrow: v})} testId="hero-eyebrow" />
            <Field label="Titre — ligne 1 (blanc)" value={salonForm.hero_title_line1} onChange={(v)=>setSalonForm({...salonForm, hero_title_line1: v})} testId="hero-line1" />
            <Field label="Titre — ligne 2 (dorée)" value={salonForm.hero_title_line2} onChange={(v)=>setSalonForm({...salonForm, hero_title_line2: v})} testId="hero-line2" />
            <Field label="Titre — ligne 3 (blanc)" value={salonForm.hero_title_line3} onChange={(v)=>setSalonForm({...salonForm, hero_title_line3: v})} testId="hero-line3" />
            <FieldArea label="Texte de présentation (sous le titre — Entrée pour aller à la ligne)" value={salonForm.description} onChange={(v)=>setSalonForm({...salonForm, description: v})} testId="hero-description" />
            <Field label="Image de bannière (URL)" value={salonForm.banner_url} onChange={(v)=>setSalonForm({...salonForm, banner_url: v})} testId="salon-banner" />
            <Field label="Note affichée (ex : 4,9 / 5 · 320 avis) — laisser vide pour masquer" value={content.hero_rating} onChange={(v)=>cset("hero_rating", v)} testId="content-hero-rating" />
            <SaveBtn onClick={saveSalon} label="Enregistrer le bandeau" testId="hero-save" />
          </div>
        </section>

        {/* 02 — Chiffres clés */}
        <section data-testid="settings-stats">
          <SectionHeader n="02" title="Chiffres clés" desc="La barre de statistiques affichée juste sous le bandeau." />
          <div className="space-y-4">
            {(salonForm.stats || []).map((st, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end p-5 bg-[#141311] border border-[#2C2A26]" data-testid={`stat-row-${i}`}>
                <div className="md:col-span-4"><Field label="Chiffre" value={st.num} onChange={(v)=>updateStat(i, "num", v)} testId={`stat-num-${i}`} /></div>
                <div className="md:col-span-8"><Field label="Libellé" value={st.label} onChange={(v)=>updateStat(i, "label", v)} testId={`stat-label-${i}`} /></div>
              </div>
            ))}
          </div>
          <SaveBtn onClick={saveSalon} label="Enregistrer les chiffres" testId="stats-save" />
        </section>

        {/* 03 — Prestations */}
        <section data-testid="settings-services">
          <SectionHeader n="03" title="Prestations & tarifs" desc="Titre de la section, tarifs/durées et images des cartes." />
          <SectionTitleEditor sectionKey="services" sections={salonForm.sections} onChange={updateSection} />
          <div className="divide-y divide-[#2C2A26] border-y border-[#2C2A26] mt-6">
            {services.map((s) => (
              <ServiceRow key={s.id} service={s} onSave={(patch) => updateService(s.id, patch)} />
            ))}
          </div>
          <Block title="Images des cartes de prestations (une par carte, dans l'ordre)">
            {(content.service_images || []).map((src, i) => (
              <div key={i} className="flex gap-2 items-end">
                <div className="flex-1"><Field label={`Image prestation ${i + 1}`} value={src} onChange={(v)=>csetArr("service_images", i, v)} testId={`content-service-image-${i}`} /></div>
                <RemoveInline onClick={()=>cremoveArr("service_images", i)} testId={`content-service-image-remove-${i}`} />
              </div>
            ))}
            <AddBtn label="Ajouter une image" onClick={()=>caddArr("service_images", "")} testId="content-service-image-add" />
          </Block>
          <SaveBtn onClick={saveSalon} label="Enregistrer le titre & les images" testId="services-save" />
        </section>

        {/* 04 — Galerie */}
        <section data-testid="settings-gallery">
          <SectionHeader n="04" title="Galerie de styles" />
          <SectionTitleEditor sectionKey="gallery" sections={salonForm.sections} onChange={updateSection} />
          <div className="mt-6 space-y-4">
            <FieldArea label="Texte d'introduction" value={content.gallery_intro} onChange={(v)=>cset("gallery_intro", v)} testId="content-gallery-intro" />
            {(content.gallery || []).map((g, i) => (
              <div key={i} className="p-4 bg-[#0A0908] border border-[#2C2A26] relative" data-testid={`content-gallery-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Nom" value={g.name} onChange={(v)=>csetArr("gallery", i, { name: v })} testId={`content-gallery-name-${i}`} />
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Prestation liée</Label>
                    <select value={g.service || ""} onChange={(e)=>csetArr("gallery", i, { service: e.target.value })} className="mt-2 w-full bg-[#141311] border border-[#2C2A26] p-2.5 text-sm text-[#F4F0EA]" data-testid={`content-gallery-service-${i}`}>
                      <option value="">— Aucune —</option>
                      {services.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2"><Field label="Description" value={g.desc} onChange={(v)=>csetArr("gallery", i, { desc: v })} testId={`content-gallery-desc-${i}`} /></div>
                  <div className="md:col-span-2"><Field label="Image (URL)" value={g.img} onChange={(v)=>csetArr("gallery", i, { img: v })} testId={`content-gallery-img-${i}`} /></div>
                </div>
                <RemoveCorner onClick={()=>cremoveArr("gallery", i)} testId={`content-gallery-remove-${i}`} />
              </div>
            ))}
            <AddBtn label="Ajouter une photo" onClick={()=>caddArr("gallery", { name: "", desc: "", img: "", service: "" })} testId="content-gallery-add" />
          </div>
          <SaveBtn onClick={saveSalon} label="Enregistrer la galerie" testId="gallery-save" />
        </section>

        {/* 05 — Qui sommes-nous */}
        <section data-testid="settings-about">
          <SectionHeader n="05" title="Qui sommes-nous" />
          <SectionTitleEditor sectionKey="about" sections={salonForm.sections} onChange={updateSection} />
          <div className="mt-6 space-y-4">
            <Field label="Image (URL)" value={content.about_image} onChange={(v)=>cset("about_image", v)} testId="content-about-image" />
            {(content.about_paragraphs || []).map((p, i) => (
              <div key={i} className="relative">
                <FieldArea label={`Paragraphe ${i + 1}`} value={p} onChange={(v)=>csetArr("about_paragraphs", i, v)} testId={`content-about-paragraph-${i}`} />
                <RemoveCorner onClick={()=>cremoveArr("about_paragraphs", i)} testId={`content-about-remove-${i}`} />
              </div>
            ))}
            <AddBtn label="Ajouter un paragraphe" onClick={()=>caddArr("about_paragraphs", "")} testId="content-about-add" />
          </div>
          <SaveBtn onClick={saveSalon} label="Enregistrer" testId="about-save" />
        </section>

        {/* 06 — Nos valeurs */}
        <section data-testid="settings-values">
          <SectionHeader n="06" title="Nos valeurs" />
          <SectionTitleEditor sectionKey="values" sections={salonForm.sections} onChange={updateSection} />
          <div className="mt-6 space-y-4">
            {(content.values || []).map((v, i) => (
              <div key={i} className="p-4 bg-[#0A0908] border border-[#2C2A26] relative" data-testid={`content-value-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                  <div className="md:col-span-2"><Field label="N°" value={v.num} onChange={(val)=>csetArr("values", i, { num: val })} testId={`content-value-num-${i}`} /></div>
                  <div className="md:col-span-10"><Field label="Titre" value={v.title} onChange={(val)=>csetArr("values", i, { title: val })} testId={`content-value-title-${i}`} /></div>
                  <div className="md:col-span-12"><FieldArea label="Texte" value={v.text} onChange={(val)=>csetArr("values", i, { text: val })} testId={`content-value-text-${i}`} /></div>
                </div>
                <RemoveCorner onClick={()=>cremoveArr("values", i)} testId={`content-value-remove-${i}`} />
              </div>
            ))}
            <AddBtn label="Ajouter une valeur" onClick={()=>caddArr("values", { num: "", title: "", text: "" })} testId="content-value-add" />
          </div>
          <SaveBtn onClick={saveSalon} label="Enregistrer les valeurs" testId="values-save" />
        </section>

        {/* 07 — Notre engagement */}
        <section data-testid="settings-commitment">
          <SectionHeader n="07" title="Notre engagement" />
          <SectionTitleEditor sectionKey="commitment" sections={salonForm.sections} onChange={updateSection} />
          <div className="mt-6 space-y-4">
            <Field label="Image (URL)" value={content.commitment_image} onChange={(v)=>cset("commitment_image", v)} testId="content-commitment-image" />
            {(content.commitment_items || []).map((it, i) => (
              <div key={i} className="p-4 bg-[#0A0908] border border-[#2C2A26] relative" data-testid={`content-commitment-${i}`}>
                <Field label="Titre" value={it.title} onChange={(val)=>csetArr("commitment_items", i, { title: val })} testId={`content-commitment-title-${i}`} />
                <div className="mt-3"><FieldArea label="Description" value={it.desc} onChange={(val)=>csetArr("commitment_items", i, { desc: val })} testId={`content-commitment-desc-${i}`} /></div>
                <RemoveCorner onClick={()=>cremoveArr("commitment_items", i)} testId={`content-commitment-remove-${i}`} />
              </div>
            ))}
            <AddBtn label="Ajouter un engagement" onClick={()=>caddArr("commitment_items", { title: "", desc: "" })} testId="content-commitment-add" />
            <div className="pt-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Mots-clés (étiquettes)</Label>
              <div className="mt-3 space-y-2">
                {(content.commitment_tags || []).map((t, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input value={t} onChange={(e)=>csetArr("commitment_tags", i, e.target.value)} className="bg-[#0A0908] border-[#2C2A26]" data-testid={`content-tag-${i}`} />
                    <RemoveInline onClick={()=>cremoveArr("commitment_tags", i)} testId={`content-tag-remove-${i}`} />
                  </div>
                ))}
              </div>
              <AddBtn label="Ajouter un mot-clé" onClick={()=>caddArr("commitment_tags", "")} testId="content-tag-add" />
            </div>
          </div>
          <SaveBtn onClick={saveSalon} label="Enregistrer l'engagement" testId="commitment-save" />
        </section>

        {/* 08 — Témoignages */}
        <section data-testid="settings-testimonials">
          <SectionHeader n="08" title="Témoignages" />
          <SectionTitleEditor sectionKey="testimonials" sections={salonForm.sections} onChange={updateSection} />
          <div className="mt-6 space-y-4">
            <Field label="Note affichée au-dessus des avis (ex : 4,9 / 5 · 320 avis)" value={content.testimonials_rating} onChange={(v)=>cset("testimonials_rating", v)} testId="content-testimonials-rating" />
            {(salonForm.testimonials || []).map((t, i) => (
              <div key={i} className="p-5 bg-[#141311] border border-[#2C2A26]" data-testid={`testimonial-row-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-4"><Field label="Nom" value={t.name} onChange={(v)=>updateTestimonial(i, "name", v)} testId={`testimonial-name-${i}`} /></div>
                  <div className="md:col-span-3">
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Étoiles (1-5)</Label>
                    <Input type="number" min="1" max="5" value={t.stars ?? 5} onChange={(e)=>updateTestimonial(i, "stars", Math.max(1, Math.min(5, parseInt(e.target.value)||5)))} className="mt-2 bg-[#141311] border-[#2C2A26]" data-testid={`testimonial-stars-${i}`} />
                  </div>
                  <div className="md:col-span-5 flex items-end justify-end">
                    <button onClick={()=>removeTestimonial(i)} className="text-[#A39E93] hover:text-[#8C3131] inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em]" data-testid={`testimonial-delete-${i}`}>
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Supprimer
                    </button>
                  </div>
                  <div className="md:col-span-12"><FieldArea label="Avis" value={t.text} onChange={(v)=>updateTestimonial(i, "text", v)} testId={`testimonial-text-${i}`} /></div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-4 mt-6">
            <button onClick={addTestimonial} className="btn-secondary" data-testid="testimonial-add"><Plus className="w-4 h-4" /> Ajouter un avis</button>
            <SaveBtn onClick={saveSalon} label="Enregistrer les témoignages" testId="testimonials-save" />
          </div>
        </section>

        {/* 09 — Récompenses */}
        <section data-testid="settings-awards">
          <SectionHeader n="09" title="Récompenses / distinctions" />
          <div className="space-y-2">
            {(content.awards || []).map((a, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input value={a} onChange={(e)=>csetArr("awards", i, e.target.value)} className="bg-[#0A0908] border-[#2C2A26]" data-testid={`content-award-${i}`} />
                <RemoveInline onClick={()=>cremoveArr("awards", i)} testId={`content-award-remove-${i}`} />
              </div>
            ))}
          </div>
          <AddBtn label="Ajouter une distinction" onClick={()=>caddArr("awards", "")} testId="content-award-add" />
          <SaveBtn onClick={saveSalon} label="Enregistrer les récompenses" testId="awards-save" />
        </section>

        {/* 10 — Contact & Horaires */}
        <section data-testid="settings-contact">
          <SectionHeader n="10" title="Contact & Horaires" desc="Coordonnées, horaires d'ouverture et fermetures exceptionnelles." />
          <SectionTitleEditor sectionKey="contact" sections={salonForm.sections} onChange={updateSection} />
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Adresse" value={salonForm.address} onChange={(v)=>setSalonForm({...salonForm, address: v})} testId="salon-address" />
            <Field label="Téléphone" value={salonForm.phone} onChange={(v)=>setSalonForm({...salonForm, phone: v})} testId="salon-phone" />
            <Field label="Email" value={salonForm.email} onChange={(v)=>setSalonForm({...salonForm, email: v})} testId="salon-email" />
            <Field label="Google Maps (URL)" value={salonForm.map_url} onChange={(v)=>setSalonForm({...salonForm, map_url: v})} testId="salon-map" />
          </div>
          <SaveBtn onClick={saveSalon} label="Enregistrer les coordonnées" testId="contact-save" />

          <div className="mt-12" data-testid="settings-hours">
            <SectionTitleEditor sectionKey="hours" sections={salonForm.sections} onChange={updateSection} />
            <div className="mt-6"><HoursEditor hours={salon.hours} onSave={saveHours} /></div>
          </div>

          <div className="mt-12" data-testid="settings-closures">
            <div className="font-mono-caps text-[10px] tracking-[0.3em] text-[#C5A059] uppercase mb-4">Fermetures exceptionnelles</div>
            <form onSubmit={addClosure} className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-8 p-6 bg-[#141311] border border-[#2C2A26]" data-testid="closure-form">
              <Input placeholder="Titre (ex: Congés)" value={newClosure.title} onChange={(e)=>setNewClosure({...newClosure, title: e.target.value})} required className="md:col-span-4 bg-[#0A0908] border-[#2C2A26]" data-testid="closure-title" />
              <Input type="date" value={newClosure.date_start} onChange={(e)=>setNewClosure({...newClosure, date_start: e.target.value})} required className="md:col-span-3 bg-[#0A0908] border-[#2C2A26]" data-testid="closure-start" />
              <Input type="date" value={newClosure.date_end} onChange={(e)=>setNewClosure({...newClosure, date_end: e.target.value})} required className="md:col-span-3 bg-[#0A0908] border-[#2C2A26]" data-testid="closure-end" />
              <button type="submit" className="btn-primary justify-center md:col-span-2" data-testid="closure-add"><Plus className="w-4 h-4" /> Ajouter</button>
            </form>
            {closures.length === 0 ? (
              <p className="text-[#6B6760] text-sm">Aucune fermeture programmée.</p>
            ) : (
              <div className="divide-y divide-[#2C2A26] border-y border-[#2C2A26]">
                {closures.map(c => (
                  <div key={c.id} className="flex items-center justify-between py-4" data-testid={`closure-row-${c.id}`}>
                    <div>
                      <div className="font-medium">{c.title}</div>
                      <div className="text-xs text-[#A39E93]">{c.date_start} → {c.date_end} {c.all_day ? "· Toute la journée" : `· ${c.time_start}–${c.time_end}`}</div>
                    </div>
                    <button onClick={() => deleteClosure(c.id)} className="text-[#A39E93] hover:text-[#8C3131]" data-testid={`closure-delete-${c.id}`}>
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* 11 — Appel à l'action */}
        <section data-testid="settings-cta">
          <SectionHeader n="11" title="Appel à l'action (bas de page)" />
          <SectionTitleEditor sectionKey="cta" sections={salonForm.sections} onChange={updateSection} />
          <div className="mt-6">
            <FieldArea label="Sous-titre sous le bouton" value={content.cta_subtitle} onChange={(v)=>cset("cta_subtitle", v)} testId="content-cta-subtitle" />
          </div>
          <SaveBtn onClick={saveSalon} label="Enregistrer" testId="cta-save" />
        </section>

        {/* 12 — Pied de page */}
        <section data-testid="settings-footer">
          <SectionHeader n="12" title="Pied de page" desc="Le bas de page de votre site." />
          <div className="grid grid-cols-1 gap-5 max-w-2xl">
            <Field label="Texte de copyright — {year} = année automatique, {name} = nom du salon" value={content.footer_copyright} onChange={(v)=>cset("footer_copyright", v)} testId="content-footer-copyright" />
            <Field label="Libellé du lien « Espace propriétaire »" value={content.footer_owner_link} onChange={(v)=>cset("footer_owner_link", v)} testId="content-footer-link" />
            <SaveBtn onClick={saveSalon} label="Enregistrer le pied de page" testId="footer-save" />
          </div>
        </section>

        {/* Réglages techniques (non visibles directement sur la page) */}
        <div className="border-t border-[#2C2A26] pt-12">
          <div className="text-[10px] tracking-[0.4em] text-[#C5A059] uppercase mb-2">Réglages techniques</div>
          <h2 className="font-serif text-3xl font-light">Coulisses</h2>
          <p className="text-sm text-[#A39E93] mt-2">Ces réglages n&apos;apparaissent pas directement comme une section de la page d&apos;accueil.</p>
        </div>

        {/* Identité */}
        <section data-testid="settings-salon">
          <h3 className="font-serif text-2xl font-light mb-6">Identité du salon (nom & logo)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Nom du salon" value={salonForm.name} onChange={(v)=>setSalonForm({...salonForm, name: v})} testId="salon-name" />
            <Field label="Phrase d'accroche" value={salonForm.tagline} onChange={(v)=>setSalonForm({...salonForm, tagline: v})} testId="salon-tagline" />
            <Field label="URL du logo" value={salonForm.logo_url} onChange={(v)=>setSalonForm({...salonForm, logo_url: v})} testId="salon-logo-url" className="md:col-span-2" />
          </div>
          <SaveBtn onClick={saveSalon} label="Enregistrer l'identité" testId="salon-save" />
        </section>

        {/* Coiffeurs */}
        <StaffManager staff={staff} setStaff={setStaff} />

        {/* Emails & notifications */}
        {emailForm && (
        <section data-testid="settings-emails">
          <h3 className="font-serif text-2xl font-light mb-3">Emails & notifications</h3>
          <p className="text-sm text-[#A39E93] mb-8">Connectez votre boîte mail (SMTP) pour envoyer automatiquement les confirmations, annulations, modifications et rappels (24h avant) à vos clients — avec leur lien de gestion de rendez-vous.</p>

          <div className="space-y-5 max-w-3xl">
            <div className="flex items-center gap-3 p-5 bg-[#141311] border border-[#2C2A26]">
              <Switch checked={emailForm.emails_enabled} onCheckedChange={(v) => setEmailForm({ ...emailForm, emails_enabled: v })} data-testid="email-enabled" />
              <span className="text-sm">{emailForm.emails_enabled ? "Emails activés" : "Emails désactivés"}</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Nom d&apos;expéditeur</Label>
                <Input value={emailForm.email_from_name} onChange={(e) => setEmailForm({ ...emailForm, email_from_name: e.target.value })} placeholder="Mon Salon" className="mt-2 bg-[#0A0908] border-[#2C2A26]" data-testid="email-from-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Email d&apos;expéditeur</Label>
                <Input value={emailForm.email_from_address} onChange={(e) => setEmailForm({ ...emailForm, email_from_address: e.target.value })} placeholder="contact@monsalon.fr" className="mt-2 bg-[#0A0908] border-[#2C2A26]" data-testid="email-from-address" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">URL du site (pour les liens dans les emails)</Label>
                <Input value={emailForm.site_url} onChange={(e) => setEmailForm({ ...emailForm, site_url: e.target.value })} placeholder="https://www.monsalon.fr" className="mt-2 bg-[#0A0908] border-[#2C2A26]" data-testid="email-site-url" />
              </div>
            </div>

            <div className="p-5 bg-[#141311] border border-[#2C2A26] space-y-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Serveur SMTP</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Serveur (hôte)</Label>
                  <Input value={emailForm.smtp_host} onChange={(e) => setEmailForm({ ...emailForm, smtp_host: e.target.value })} placeholder="smtp.gmail.com" className="mt-2 bg-[#0A0908] border-[#2C2A26]" data-testid="email-smtp-host" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Port</Label>
                  <Input type="number" value={emailForm.smtp_port} onChange={(e) => setEmailForm({ ...emailForm, smtp_port: parseInt(e.target.value) || 587 })} className="mt-2 bg-[#0A0908] border-[#2C2A26]" data-testid="email-smtp-port" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Sécurité</Label>
                  <select value={emailForm.smtp_security} onChange={(e) => setEmailForm({ ...emailForm, smtp_security: e.target.value })} className="mt-2 w-full bg-[#0A0908] border border-[#2C2A26] p-2.5 text-sm text-[#F4F0EA]" data-testid="email-smtp-security">
                    <option value="tls">STARTTLS (port 587)</option>
                    <option value="ssl">SSL/TLS (port 465)</option>
                    <option value="none">Aucune</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Identifiant</Label>
                  <Input value={emailForm.smtp_user} onChange={(e) => setEmailForm({ ...emailForm, smtp_user: e.target.value })} placeholder="contact@gmail.com" className="mt-2 bg-[#0A0908] border-[#2C2A26]" data-testid="email-smtp-user" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Mot de passe d&apos;application</Label>
                  <Input type="password" value={emailForm.smtp_password} onChange={(e) => setEmailForm({ ...emailForm, smtp_password: e.target.value })} placeholder={emailPwdSet ? "•••••••• (enregistré)" : "Mot de passe"} className="mt-2 bg-[#0A0908] border-[#2C2A26]" data-testid="email-smtp-password" />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Email de notification du propriétaire</Label>
              <Input value={emailForm.owner_notify_email} onChange={(e) => setEmailForm({ ...emailForm, owner_notify_email: e.target.value })} placeholder="vous@monsalon.fr" className="mt-2 bg-[#0A0908] border-[#2C2A26]" data-testid="email-owner-notify" />
              <p className="text-xs text-[#6B6760] mt-2">Vous recevrez un email à chaque nouvelle réservation.</p>
            </div>

            <button onClick={saveEmails} className="btn-primary" data-testid="emails-save"><Save className="w-4 h-4" strokeWidth={1.5} /> Enregistrer la configuration</button>

            <div className="p-5 bg-[#141311] border border-[#2C2A26] mt-2">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-3">Tester l&apos;envoi</div>
              <p className="text-xs text-[#6B6760] mb-3">Enregistrez d&apos;abord la configuration, puis envoyez-vous un email de test.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="adresse@email.fr" className="bg-[#0A0908] border-[#2C2A26]" data-testid="email-test-to" />
                <button onClick={sendTest} className="btn-secondary whitespace-nowrap" data-testid="email-test-send">Envoyer un email de test</button>
              </div>
            </div>
          </div>
        </section>
        )}

        {/* Mentions légales & RGPD */}
        <section data-testid="settings-legal">
          <SectionHeader title="Mentions légales & RGPD" desc="Ces informations alimentent vos pages Mentions légales, Confidentialité et CGV (obligatoires en France). L'adresse, le téléphone et l'email du salon sont repris automatiquement." />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl">
            <Field label="Dénomination / raison sociale" value={content.legal_company} onChange={(v)=>cset("legal_company", v)} testId="legal-company" />
            <Field label="Forme juridique (ex : EI, SARL…)" value={content.legal_status} onChange={(v)=>cset("legal_status", v)} testId="legal-status" />
            <Field label="SIRET" value={content.legal_siret} onChange={(v)=>cset("legal_siret", v)} testId="legal-siret" />
            <Field label="Capital social (optionnel)" value={content.legal_capital} onChange={(v)=>cset("legal_capital", v)} testId="legal-capital" />
            <Field label="TVA intracommunautaire (optionnel)" value={content.legal_vat} onChange={(v)=>cset("legal_vat", v)} testId="legal-vat" />
            <Field label="Directeur de la publication" value={content.legal_director} onChange={(v)=>cset("legal_director", v)} testId="legal-director" />
            <Field label="Hébergeur" value={content.legal_host} onChange={(v)=>cset("legal_host", v)} testId="legal-host" />
            <Field label="Coordonnées de l'hébergeur (optionnel)" value={content.legal_host_details} onChange={(v)=>cset("legal_host_details", v)} testId="legal-host-details" />
            <Field label="Email contact RGPD / DPO (optionnel)" value={content.dpo_email} onChange={(v)=>cset("dpo_email", v)} testId="legal-dpo-email" className="md:col-span-2" />
          </div>
          <SaveBtn onClick={saveSalon} label="Enregistrer les mentions légales" testId="legal-save" />
        </section>
      </div>
    </div>
  );
}

/* ---------------- Reusable bits ---------------- */
function SectionHeader({ n, title, desc }) {
  return (
    <div className="mb-8">
      <div className="flex items-baseline gap-3">
        {n && <span className="font-display text-2xl text-[#C5A059]">{n}</span>}
        <h2 className="font-serif text-3xl font-light">{title}</h2>
      </div>
      {desc && <p className="text-sm text-[#A39E93] mt-2">{desc}</p>}
    </div>
  );
}

function SectionTitleEditor({ sectionKey, sections, onChange }) {
  const cfg = sections?.[sectionKey] || {};
  return (
    <div className="p-5 bg-[#141311] border border-[#2C2A26] space-y-4" data-testid={`section-edit-${sectionKey}`}>
      <div className="font-mono-caps text-[10px] tracking-[0.3em] text-[#C5A059] uppercase">Titre de la section</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Petit texte au-dessus</Label>
          <Input value={cfg.eyebrow || ""} onChange={(e)=>onChange(sectionKey, "eyebrow", e.target.value)} className="mt-2 bg-[#0A0908] border-[#2C2A26]" data-testid={`section-${sectionKey}-eyebrow`} />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Grand titre (Entrée ou | pour aller à la ligne)</Label>
          <textarea value={cfg.title || ""} onChange={(e)=>onChange(sectionKey, "title", e.target.value)} rows={2} className="mt-2 w-full bg-[#0A0908] border border-[#2C2A26] p-2.5 text-sm text-[#F4F0EA]" data-testid={`section-${sectionKey}-title`} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Taille</Label>
          <select value={cfg.size || "L"} onChange={(e)=>onChange(sectionKey, "size", e.target.value)} className="mt-2 w-full bg-[#0A0908] border border-[#2C2A26] p-2.5 text-sm text-[#F4F0EA]" data-testid={`section-${sectionKey}-size`}>
            {SIZE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Couleur</Label>
          <select value={cfg.color || "white"} onChange={(e)=>onChange(sectionKey, "color", e.target.value)} className="mt-2 w-full bg-[#0A0908] border border-[#2C2A26] p-2.5 text-sm text-[#F4F0EA]" data-testid={`section-${sectionKey}-color`}>
            {COLOR_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="mt-6 space-y-3">
      <div className="font-mono-caps text-[10px] tracking-[0.3em] text-[#C5A059] uppercase">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, testId, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">{label}</Label>
      <Input value={value ?? ""} onChange={(e)=>onChange(e.target.value)} className="mt-2 bg-[#141311] border-[#2C2A26]" data-testid={testId} />
    </div>
  );
}

function FieldArea({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">{label}</Label>
      <textarea value={value ?? ""} onChange={(e)=>onChange(e.target.value)} rows={3} className="mt-2 w-full bg-[#141311] border border-[#2C2A26] p-3 text-sm text-[#F4F0EA]" data-testid={testId} />
    </div>
  );
}

function AddBtn({ label, onClick, testId }) {
  return (
    <button onClick={onClick} className="btn-secondary text-xs mt-2" data-testid={testId}>
      <Plus className="w-4 h-4" /> {label}
    </button>
  );
}

function RemoveInline({ onClick, testId }) {
  return (
    <button onClick={onClick} className="text-[#A39E93] hover:text-[#8C3131] shrink-0" data-testid={testId}>
      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
    </button>
  );
}

function RemoveCorner({ onClick, testId }) {
  return (
    <button onClick={onClick} className="absolute top-3 right-3 text-[#A39E93] hover:text-[#8C3131]" data-testid={testId}>
      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
    </button>
  );
}

function SaveBtn({ onClick, label, testId }) {
  return (
    <button onClick={onClick} className="btn-primary mt-6 self-start" data-testid={testId}>
      <Save className="w-4 h-4" strokeWidth={1.5} /> {label}
    </button>
  );
}

function ServiceRow({ service, onSave }) {
  const [price, setPrice] = useState(service.price);
  const [duration, setDuration] = useState(service.duration);
  const [active, setActive] = useState(service.active);
  const dirty = price !== service.price || duration !== service.duration || active !== service.active;
  return (
    <div className="grid grid-cols-12 gap-4 py-5 items-center" data-testid={`service-row-${service.id}`}>
      <div className="col-span-12 md:col-span-4 font-serif text-xl">{service.name}</div>
      <div className="col-span-5 md:col-span-3">
        <Label className="text-[10px] uppercase tracking-[0.2em] text-[#A39E93]">Durée (min)</Label>
        <Input type="number" min="5" step="5" value={duration} onChange={(e)=>setDuration(parseInt(e.target.value)||0)} className="mt-1 bg-[#141311] border-[#2C2A26]" data-testid={`service-duration-${service.id}`} />
      </div>
      <div className="col-span-5 md:col-span-3">
        <Label className="text-[10px] uppercase tracking-[0.2em] text-[#A39E93]">Prix (€)</Label>
        <Input type="number" min="0" step="0.5" value={price} onChange={(e)=>setPrice(parseFloat(e.target.value)||0)} className="mt-1 bg-[#141311] border-[#2C2A26]" data-testid={`service-price-${service.id}`} />
      </div>
      <div className="col-span-2 flex items-center gap-2 justify-end">
        <Switch checked={active} onCheckedChange={setActive} data-testid={`service-active-${service.id}`} />
      </div>
      <div className="col-span-12 flex justify-end">
        <button disabled={!dirty} onClick={() => onSave({ price, duration, active })} className="text-xs uppercase tracking-[0.2em] text-[#C5A059] hover:text-[#D9B873] disabled:opacity-30 inline-flex items-center gap-2" data-testid={`service-save-${service.id}`}>
          <Save className="w-3.5 h-3.5" strokeWidth={1.5} /> Enregistrer
        </button>
      </div>
    </div>
  );
}
